import { stdObject } from "../../../data/types/stdObject";
import SearchItem from "./components/SearchItem";
import CreateIcon from "./components/CreateIcon";
import useSearchBar from "./useSearchBar";
import BasicButton from "../../../components/BasicButton";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import BorderAllIcon from "@mui/icons-material/BorderAll";
import { colors } from "../../../data/colors";
import { listedAreas, shortcutCodes } from "../../../data/main";
import { screensSearch } from "../../../data/screensSearch";
import { useStructureContext } from "../../../context/StructureContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useHotkeys } from "react-hotkeys-hook";
import { useMainContext } from "../../../context/MainContext";
import { toShortcut } from "../../../utils/toShortcut";
import { sectionsList } from "../../../data/sectionsList";

export default function SearchBar() {
  const user: any = useSelector((state: RootState) => state.data.user);
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const {
    cleanAllSearch,
    saveInfoData,
    findServiceData,
    prepareToPrint,
    prepareToXLS,
    openExport,
    openImport,
    openSettings,
    searchValues,
    printList,
    callBackupInfo,
  } = useStructureContext();
  const { mainMenuheader, checkIfAllMsgClean } = useMainContext();
  const { t, type, section, mainSearch, mainSort, checkIfCreate } =
    useSearchBar();

  const realCode = section.id === "F2" ? "ART" : section.id;

  const printAccess =
    `${realCode}P` in user
      ? !!user[`${realCode}P`]
      : !!userAccess[realCode[0]]?.[`${realCode}P`];

  const searchButtonsList = [
    {
      code: "search",
      Icon: SearchIcon,
      function:
        section.id === "Z6" ? () => callBackupInfo() : () => findServiceData(),
    },
    {
      code: "save",
      Icon: SaveIcon,
      function: () => saveInfoData(),
      color: colors.green,
    },
    {
      code: "print",
      Icon: PrintIcon,
      function: prepareToPrint,
      color: colors.orange,
    },
    {
      code: "xls",
      Icon: BorderAllIcon,
      function: prepareToXLS,
      color: colors.darkGreen,
    },
    {
      code: "export",
      Icon: UploadIcon,
      function: openExport,
      color: colors.orange,
    },
    {
      code: "import",
      Icon: DownloadIcon,
      function: openImport,
      color: colors.green,
    },
    { code: "settings", Icon: SettingsIcon, function: openSettings },
  ];

  useHotkeys(
    toShortcut(shortcutCodes.print),
    () =>
      checkIfAllMsgClean() &&
      printAccess &&
      sectionsList.find((e: stdObject) => e.type === type)?.id ===
        mainMenuheader.id &&
      printList(section.code, 0),
    {
      preventDefault: true,
    },
  );

  useHotkeys(
    toShortcut(shortcutCodes.clean),
    () =>
      checkIfAllMsgClean() &&
      sectionsList.find((e: stdObject) => e.type === type)?.id ===
        mainMenuheader.id &&
      cleanAllSearch(),
    {
      preventDefault: true,
    },
  );

  const validateModule = (e: stdObject) => {
    switch (e.CODCAB) {
      case "SCHIRE":
        return !!user.IRESUC;
      case "FECIRE":
        return searchValues[section.id]?.SCHIRE?.value === "" && !user.IRESUC;
      case "HORIRE":
        return (
          searchValues[section.id]?.SCHIRE?.value !== "" && user.IRESUC === 1
        );
      case "FEC3IRE":
        return (
          (searchValues[section.id]?.SCHIRE?.value !== "" && !user.IRESUC) ||
          !!user.IRESUC
        );
      case "CODFOR":
        return searchValues[section.id]?.CODISU?.value === "SDA";
      default:
        return !["CODFOR", "HORIRE", "FECIRE", "FEC3IRE"].includes(e.CODCAB);
    }
  };

  const validateButtons = (code: string) => {
    switch (section.id) {
      case "D1":
        const searchData = searchValues[section.id];
        switch (code) {
          case "save":
            return (
              !!user.IRESUC &&
              "results" in searchData &&
              !searchData.results.recorded
            );
          case "print":
            return (
              "results" in searchData &&
              (searchData.results.recorded || !user.IRESUC)
            );
          default:
            return screensSearch[section.id][code];
        }
      default:
        return screensSearch[section.id][code];
    }
  };

  return (
    <div className="searchBar">
      {checkIfCreate() && <CreateIcon />}
      <div className="wrapBox searchItems">
        {!["C2", "C3"].includes(section.id) &&
          listedAreas.includes(section.code) && (
            <SearchItem
              sortList={{
                type: "select",
                options: mainSort,
              }}
            />
          )}
        {!["C2", "C3"].includes(section.id) &&
          mainSearch.map((e: stdObject, index: number) => (
            <SearchItem key={index} data={e} />
          ))}
        {section.id in screensSearch &&
          screensSearch[section.id].list.map(
            (e: stdObject, index: number) =>
              validateModule(e) && <SearchItem key={index} data={e} />,
          )}
        {listedAreas.includes(section.code)
          ? !["C2", "C3"].includes(section.id) && (
              <>
                <BasicButton
                  title={t("p.clean")}
                  popup={shortcutCodes.clean}
                  Icon={CleaningServicesIcon}
                  color={colors.orange}
                  onClick={() => cleanAllSearch()}
                />
                {printAccess && (
                  <BasicButton
                    title={t("p.print")}
                    popup={shortcutCodes.print}
                    Icon={PrintIcon}
                    color={colors.blue}
                    onClick={() => printList(section.code, 0)}
                  />
                )}
              </>
            )
          : section.id in screensSearch &&
            searchButtonsList.map(
              (e: stdObject, index: number) =>
                validateButtons(e.code) && (
                  <BasicButton
                    key={index}
                    title={e.code === "xls" ? "XLS" : t(`p.${e.code}`)}
                    Icon={e.Icon}
                    color={e.color ?? colors[type]}
                    onClick={e.function}
                  />
                ),
            )}
      </div>
    </div>
  );
}
